import { Box, ButtonBase, Icon, IconButton, Tooltip } from '@mui/material';
import { Component, MouseEvent, ReactElement, ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Link } from 'react-router-dom';
import { Locale, Media } from '@synergy/models';
import { PaloBonusBadge, PaloColumn, PaloListView, PaloRowAction } from '@synergy/components-palo';
import { getCurrencySymbol, padZeroLeft } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';
import AddIcon from '@mui/icons-material/AddCircleOutline';
import AttachIcon from '@mui/icons-material/AttachFile';

import { AccountPurchase } from '../models/account-purchase';
import { DocumentChainStep } from '../../../common/models/document-chain-step';
import { Inquiry } from '../models/inquiry';
import {
    InquiryDetailOffersTableDeclineModal,
    InquiryDetailOffersTableDeclineModalData,
} from './inquiry-detail-offers-table-decline-modal';
import { Offer } from '../../../common/models/offer';
import { OfferCompleteStatus } from '../../../common/models/enums/offer-complete-status';
import { OfferEditModal } from '../components/offer-edit/offer-edit-modal';
import { OfferItem } from '../../../common/models/offer-item';
import { OfferStatus } from '../../../common/models/enums/offer-status';
import { PaloBestOfferBadge } from '../components/palo-best-offer-badge';
import {
    PriceInfoAddress,
    PriceInfoSendDialog,
    supplierAccountIdsByAddress,
} from '../components/price-info-send-dialog';
import { ROUTES } from '../../sidebar-menu/navigation-drawer/routes';
import { Supplier } from '../../../common/models/supplier';
import { useDocumentChain } from '../../document-chain/palo-document-chain-context';

const c = tokens.color;

/** The most offers one price info compares. */
const MAX_PRICE_INFO_OFFERS = 3;

type OfferState = 'complete' | 'partial' | 'divergent' | 'open' | 'declined';

const stateOf = (offer: Offer): OfferState => {
    if (OfferStatus.Declined === offer.status) {
        return 'declined';
    }
    if (OfferStatus.Created === offer.status) {
        return 'open';
    }

    return OfferCompleteStatus.Complete === offer.completeStatus
        ? 'complete'
        : OfferCompleteStatus.Divergent === offer.completeStatus
        ? 'divergent'
        : 'partial';
};

const STATE_LOOK: Record<OfferState, { color: string; background: string; border: string; hint: string }> = {
    complete: { color: c.okInk, background: c.okSoft, border: c.okLine, hint: 'app.inquiries.detail.offerComplete' },
    partial: {
        color: c.warnInk,
        background: c.warnSoft,
        border: c.warnLine,
        hint: 'app.inquiries.detail.offerIncomplete',
    },
    divergent: {
        color: c.warnInk,
        background: c.warnSoft,
        border: c.warnLine,
        hint: 'app.inquiries.detail.offerDiverged',
    },
    open: { color: c.ink2, background: c.surface, border: c.line, hint: 'app.inquiries.detail.offerOpen' },
    declined: { color: c.crit, background: c.critSoft, border: c.critLine, hint: 'app.inquiries.detail.offerDeclined' },
};

/**
 * Where an offer stands, in a word. The five symbols needed a mouse-over each,
 * and the open one was a pale tick that read as done (#2256).
 */
const OfferStateChip = ({ offer }: { offer: Offer }): ReactElement => {
    const state = stateOf(offer);
    const look = STATE_LOOK[state];

    return (
        <Tooltip title={<FormattedMessage id={look.hint} />} placement="top-start">
            <Box
                component="span"
                sx={{
                    display: 'inline-block',
                    padding: '1px 9px',
                    fontSize: '11.5px',
                    whiteSpace: 'nowrap',
                    borderRadius: '999px',
                    border: `1px solid ${look.border}`,
                    color: look.color,
                    backgroundColor: look.background,
                }}
            >
                <FormattedMessage id={`app.inquiries.detail.offerState.${state}`} />
            </Box>
        </Tooltip>
    );
};

/**
 * The purchase orders made from an offer, from the chain the "Belege" tab
 * loads anyway (#2160). An order names neither the offer nor the delivery
 * address it came from, but its supplier and its customer: together they
 * point at the offer — or, where one member has two addresses with offers of
 * the same supplier, at both of them, and then both orders show.
 */
export const followUpsOf = (
    offer: Offer,
    accountPurchase: AccountPurchase | undefined,
    steps: DocumentChainStep[]
): DocumentChainStep[] =>
    OfferStatus.Declined === offer.status
        ? []
        : steps.filter(
              step =>
                  'purchase-orders' === step.type &&
                  step.supplier === offer.supplierAccount?.name &&
                  (!step.customer || step.customer === accountPurchase?.customerAccount?.name)
          );

const OfferFollowUps = ({ offer, accountPurchase }: { offer: Offer; accountPurchase?: AccountPurchase }) => {
    const { steps } = useDocumentChain();
    const orders = followUpsOf(offer, accountPurchase, steps);

    if (0 === orders.length) {
        return (
            <Box component="span" sx={{ color: c.ink3 }}>
                –
            </Box>
        );
    }

    return (
        <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {orders.map(order => (
                <Link
                    key={order.key}
                    to={ROUTES.purchaseOrdersDetail.replace(':id', order.id.toString())}
                    style={{ color: c.accentHi, textDecoration: 'none', whiteSpace: 'nowrap' }}
                >
                    <FormattedMessage id="app.document.purchaseOrder" /> {order.number ?? `#${order.id}`}
                </Link>
            ))}
        </Box>
    );
};

interface InquiryDetailOffersTableProps {
    currency: string;
    locale: Locale;
    offers: Offer[];
    suppliers: Supplier[];
    selectedOffers: number[];
    recommendedOfferId?: number;
    bestOfferId: number;
    accountPurchase?: AccountPurchase;
    /** The same member's other delivery addresses in this inquiry, each with what is ticked there. */
    memberSelections?: PriceInfoAddress[];
    /** What goes with every price info of the inquiry, for the check before sending. */
    customerMedia: Media[];
    /** How many customers the inquiry has: the files above go to all of them. */
    customerCount: number;
    readonly: boolean;
    globalState?: Partial<Inquiry>;
    onSelectionChanged?: (selectedOffers: Offer[]) => void;
    onRecommendationChanged?: (recommendedOfferId: number, isRecommended: boolean) => void;
    onOfferDecline?: (offerId: number, subject: string, message: string) => void;
    onOfferChanged?: (offer: Partial<Offer>, neFiles: File[], mediaToRemove: Media[]) => void;
    onOfferCreated?: (offer: Partial<Offer>) => void;
    onSendPriceComparison?: (
        accountPurchaseId: number,
        supplierAccounts: number[],
        allDeliveryAddresses: boolean,
        supplierAccountIdsByAddress?: Record<number, number[]>
    ) => void;
}

interface InquiryDetailOffersTableState {
    warningDialogData?: InquiryDetailOffersTableDeclineModalData;
    warningDialogOfferId?: number;
    warningDialogMultipleOffers: boolean;
    editModalVisible: boolean;
    editModalOffer?: Offer;
    showPriceComparison: boolean;
}

/**
 * The offers of one delivery address, inside its card on "Angebote &
 * Preisspiegel": what each one is worth, where it stands, what was ordered
 * from it, and one main action — check the price info and send it (#2256).
 */
export class InquiryDetailOffersTable extends Component<InquiryDetailOffersTableProps, InquiryDetailOffersTableState> {
    public state: InquiryDetailOffersTableState = {
        warningDialogMultipleOffers: false,
        editModalOffer: undefined,
        editModalVisible: false,
        showPriceComparison: false,
    };

    private get columns(): PaloColumn<Offer>[] {
        const symbol = getCurrencySymbol(this.props.currency, this.props.locale);

        return [
            {
                key: 'status',
                width: '110px',
                type: 'status',
                head: <FormattedMessage id="app.table.column.status" />,
                cell: offer => <OfferStateChip offer={offer} />,
            },
            {
                key: 'number',
                width: '64px',
                type: 'documentNumber',
                head: <FormattedMessage id="app.table.column.numberAbbreviation" />,
                cell: offer => padZeroLeft(offer.id, 4),
            },
            {
                key: 'name',
                type: 'name',
                // The badge goes under the name rather than being cut off.
                wrap: true,
                head: <FormattedMessage id="app.table.column.supplier" />,
                cell: offer => (
                    <Box
                        component="span"
                        sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px 8px' }}
                    >
                        <span>{offer.supplierAccount.name}</span>
                        {offer.id === this.props.bestOfferId && <PaloBestOfferBadge />}
                    </Box>
                ),
            },
            {
                key: 'bonus',
                width: '104px',
                type: 'flag',
                head: <FormattedMessage id="app.table.column.bonus" />,
                cell: offer => (
                    <PaloBonusBadge
                        conditions={offer.supplierBonusConditions as never}
                        at={this.props.globalState?.createdAt}
                        supplierName={offer.supplierAccount.name}
                    />
                ),
            },
            {
                key: 'attachments',
                width: '28px',
                type: 'icon',
                head: '',
                cell: offer =>
                    offer.media && offer.media.length > 0 ? (
                        <AttachIcon sx={{ fontSize: 17, color: c.accentHi }} />
                    ) : null,
            },
            {
                key: 'price',
                width: '96px',
                type: 'amount',
                head: <FormattedMessage id="app.table.column.priceWithValue" values={{ value: symbol }} />,
                cell: offer => this.amountOf(offer, offer.getTotal()),
            },
            {
                key: 'deliveryCost',
                width: '96px',
                type: 'amount',
                head: <FormattedMessage id="app.table.column.deliveryCost" values={{ value: symbol }} />,
                cell: offer => this.amountOf(offer, offer.deliveryPrice),
            },
            {
                key: 'surcharge',
                width: '104px',
                type: 'amount',
                head: <FormattedMessage id="app.inquiries.detail.column.surcharge" values={{ value: symbol }} />,
                cell: offer => this.amountOf(offer, offer.surcharge || 0),
            },
            {
                key: 'total',
                width: '104px',
                type: 'amount',
                highlighted: true,
                head: <FormattedMessage id="app.inquiries.detail.column.total" values={{ value: symbol }} />,
                cell: offer => (
                    <Box component="span" sx={offer.id === this.props.bestOfferId ? { fontWeight: 700 } : undefined}>
                        {this.amountOf(offer, offer.getTotalWithCosts())}
                    </Box>
                ),
            },
            {
                key: 'recommendation',
                width: '92px',
                type: 'flag',
                head: <FormattedMessage id="app.inquiries.detail.recommendation" />,
                // Only an offer handed in, or already taken, can be the one
                // recommended to the member.
                cell: offer =>
                    OfferStatus.Pending === offer.status || OfferStatus.Accepted === offer.status ? (
                        <IconButton size="small" onClick={this.handleRecommendationClicked(offer)}>
                            <Icon
                                className="icon-star"
                                sx={{
                                    fontSize: 18,
                                    color: this.props.recommendedOfferId === offer.id ? c.accent : c.line,
                                }}
                            />
                        </IconButton>
                    ) : null,
            },
            {
                key: 'followUp',
                width: '140px',
                type: 'documentNumber',
                head: <FormattedMessage id="app.inquiries.detail.followUp" />,
                cell: offer => <OfferFollowUps offer={offer} accountPurchase={this.props.accountPurchase} />,
            },
        ];
    }

    private amountOf = (_offer: Offer, value: number): ReactNode => (
        <FormattedNumber value={value} minimumFractionDigits={2} />
    );

    private offerActions = (offer: Offer): PaloRowAction[] => [
        {
            label: this.props.readonly ? (
                <FormattedMessage id="app.inquiries.detail.action.showOffer" />
            ) : (
                <FormattedMessage id="app.inquiries.detail.action.editOffer" />
            ),
            onClick: this.handleEditOfferAction(offer),
        },
        // Declining stays open on a completed inquiry: the suppliers learn
        // they lost only once the order is placed, which is after completing
        // it (#2317).
        ...(OfferStatus.Declined === offer.status
            ? []
            : [
                  {
                      label: <FormattedMessage id="app.inquiries.detail.action.declineOffer" />,
                      onClick: this.handleDeclineOfferShow(offer.id),
                  },
              ]),
    ];

    private handleOfferSelected = (selectedItems: number[]) => {
        const offers = this.props.offers.filter(offer => selectedItems.includes(offer.id));
        this.props.onSelectionChanged?.(offers);
    };

    private handleRecommendationClicked = (offer: Offer) => (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        this.props.onRecommendationChanged?.(offer.id, !offer.isRecommended);
    };

    private handleDeclineOfferShow = (offerId?: number) => () => {
        this.setState({
            warningDialogOfferId: offerId,
            warningDialogMultipleOffers: offerId === undefined,
            warningDialogData: {
                inquiryNumber: this.props.globalState?.number ?? '',
                inquiryTitle: this.props.globalState?.title ?? '',
                shopOwnerName: this.props.accountPurchase?.customerAccount?.name ?? '',
            },
        });
    };

    private handleOfferDecline = (subject: string, message: string) => {
        if (this.state.warningDialogMultipleOffers) {
            this.props.selectedOffers.forEach(offerId => {
                this.props.onOfferDecline?.(offerId, subject, message);
            });
            this.props.onSelectionChanged?.([]);
        }
        if (this.state.warningDialogOfferId) {
            this.props.onOfferDecline?.(this.state.warningDialogOfferId, subject, message);
        }
        this.handleDeclineModalClose();
    };

    private handleDeclineModalClose = () => {
        this.setState({
            warningDialogData: undefined,
            warningDialogMultipleOffers: false,
            warningDialogOfferId: undefined,
        });
    };

    private handleEditOfferAction = (offer: Offer) => () => {
        this.setState({ editModalOffer: offer, editModalVisible: true });
    };

    private handleEditModalClose = () => {
        this.setState({ editModalVisible: false, editModalOffer: undefined });
    };

    private handleEditModalSave = (
        editedOffer: Partial<Offer>,
        newFiles: File[],
        mediaToRemove: Media[],
        supplierAccount?: Supplier
    ) => {
        // Is update
        if (editedOffer.id && editedOffer.id >= 0) {
            this.props.onOfferChanged?.(editedOffer, newFiles, mediaToRemove);
        } else if (this.props.onOfferCreated) {
            // Is creation of new offer -> set supplierAccount and remove offerItem ids
            editedOffer.supplierAccount = supplierAccount;
            const offerItems = editedOffer.offerItems || [];
            offerItems.forEach(item => (item.id = undefined));
            this.props.onOfferCreated(editedOffer);
        }
        this.setState({ editModalVisible: false, editModalOffer: undefined });
    };

    private handleAddOffer = () => {
        if (this.props.accountPurchase) {
            const offerItems = (this.props.accountPurchase.purchaseItems || []).map(purchaseItem => {
                const offerItem = OfferItem.fromPlainObject(purchaseItem);
                if (purchaseItem.id) {
                    offerItem.purchaseItem = { id: purchaseItem.id };
                }
                return offerItem;
            });
            offerItems.forEach(item => {
                item.isOffered = true;
                item.price = undefined;
            });

            const offer = Offer.fromPlainObject({
                offerItems,
                media: [],
                deliveryPrice: 0,
            });
            this.setState({ editModalOffer: offer, editModalVisible: true });
        }
    };

    private getSuppliersWithoutOffer = () => {
        const usedIds = this.props.offers.map(offer => offer.supplierAccount.id);
        return this.props.suppliers.filter(supplier => !usedIds.includes(supplier.id));
    };

    /**
     * The member's other delivery addresses a combined price info would add:
     * those with offers ticked on their own card. Every address used to go out
     * with the ticks of the card it was sent from (R-223).
     */
    private getCombinableAddresses = (): PriceInfoAddress[] =>
        (this.props.memberSelections ?? []).filter(
            ({ accountPurchase, offers }) =>
                !!accountPurchase.id && offers.length > 0 && offers.length <= MAX_PRICE_INFO_OFFERS
        );

    private handleSend = (priceMatrixOffers: Offer[]) => (allDeliveryAddresses: boolean) => {
        if (this.props.accountPurchase?.id && this.props.onSendPriceComparison) {
            this.props.onSendPriceComparison(
                this.props.accountPurchase.id,
                priceMatrixOffers.map(offer => offer.supplierAccount.id),
                allDeliveryAddresses,
                allDeliveryAddresses ? supplierAccountIdsByAddress(this.getCombinableAddresses()) : undefined
            );
        }
        this.setState({ showPriceComparison: false });
    };

    private renderFoot(priceMatrixOffers: Offer[]) {
        const { selectedOffers, readonly, accountPurchase, customerMedia } = this.props;
        const unusedSuppliers = this.getSuppliersWithoutOffer();
        const tooMany = selectedOffers.length > MAX_PRICE_INFO_OFFERS;
        const none = 0 === priceMatrixOffers.length;

        // A greyed-out button that says nothing reads as broken (#2214, R-43):
        // what is missing stands beside it.
        const status: ReactNode = tooMany ? (
            <Box component="span" sx={{ color: c.crit }}>
                <FormattedMessage id="app.inquiries.detail.selectThreeOffers" />
            </Box>
        ) : none ? (
            <FormattedMessage id="app.inquiries.detail.priceInfo.pickOffers" values={{ max: MAX_PRICE_INFO_OFFERS }} />
        ) : (
            <FormattedMessage
                id="app.inquiries.detail.priceInfo.summary"
                values={{
                    count: priceMatrixOffers.length,
                    contact: accountPurchase?.contact?.fullName ?? '–',
                    files: customerMedia.length,
                }}
            />
        );

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', pt: '8px' }}>
                {!readonly && (
                    <ButtonBase
                        onClick={this.handleAddOffer}
                        disabled={0 === unusedSuppliers.length}
                        sx={{
                            gap: '5px',
                            fontSize: '12.5px',
                            color: 0 === unusedSuppliers.length ? c.ink3 : c.accentHi,
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        <AddIcon sx={{ fontSize: 17 }} />
                        <FormattedMessage id="app.inquiries.detail.addOffer" />
                    </ButtonBase>
                )}
                <Box sx={{ marginLeft: 'auto', fontSize: '12px', color: c.ink3 }}>{!readonly && status}</Box>
                <ButtonBase
                    disabled={none}
                    onClick={this.handleDeclineOfferShow()}
                    sx={{
                        fontSize: '12.5px',
                        color: none ? c.ink3 : c.crit,
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    <FormattedMessage id="app.inquiries.detail.declineOffersShort" />
                </ButtonBase>
                {!readonly && (
                    <ButtonBase
                        disabled={none || tooMany}
                        onClick={() => this.setState({ showPriceComparison: true })}
                        sx={{
                            padding: '5px 14px',
                            fontSize: '12.5px',
                            fontWeight: 560,
                            borderRadius: tokens.radius.small + 'px',
                            color: none || tooMany ? c.ink3 : c.inkInvert,
                            backgroundColor: none || tooMany ? c.neutralSoft : c.accent,
                            '&:hover': { backgroundColor: none || tooMany ? c.neutralSoft : c.accentHi },
                        }}
                    >
                        <FormattedMessage id="app.inquiries.detail.priceInfo.checkAndSend" />
                    </ButtonBase>
                )}
            </Box>
        );
    }

    public render() {
        const { offers, selectedOffers, currency, locale, readonly, accountPurchase, customerMedia, customerCount } =
            this.props;

        const unselectable = offers.filter(offer => OfferStatus.Declined === offer.status).map(offer => offer.id);
        const selectable = offers.filter(offer => OfferStatus.Declined !== offer.status).map(offer => offer.id);
        const priceMatrixOffers = offers.filter(offer => selectedOffers.includes(offer.id));
        const unusedSuppliers = this.getSuppliersWithoutOffer();

        return (
            <>
                <PaloListView<Offer, number>
                    embedded={true}
                    columns={this.columns}
                    items={offers}
                    getRowId={offer => offer.id}
                    emptyLabel={<FormattedMessage id="app.inquiries.detail.noOffersYet" />}
                    rowActions={this.offerActions}
                    selection={{
                        selectedIds: selectedOffers,
                        disabledIds: unselectable,
                        disabledHint: <FormattedMessage id="app.inquiries.detail.offerNotSelectable" />,
                        allSelected: selectable.length > 0 && selectable.every(id => selectedOffers.includes(id)),
                        someSelected: selectable.some(id => selectedOffers.includes(id)),
                        onToggle: id =>
                            this.handleOfferSelected(
                                selectedOffers.includes(id)
                                    ? selectedOffers.filter(selectedId => selectedId !== id)
                                    : [...selectedOffers, id]
                            ),
                        onToggleAll: () =>
                            this.handleOfferSelected(
                                selectable.every(id => selectedOffers.includes(id)) ? [] : selectable
                            ),
                    }}
                />
                {this.renderFoot(priceMatrixOffers)}

                <InquiryDetailOffersTableDeclineModal
                    data={this.state.warningDialogData}
                    onDecline={this.handleOfferDecline}
                    onClose={this.handleDeclineModalClose}
                />

                {this.state.editModalOffer && (
                    <OfferEditModal
                        currency={currency}
                        locale={locale}
                        isVisible={this.state.editModalVisible}
                        offer={this.state.editModalOffer}
                        accountPurchaseId={this.state.editModalOffer.id ? undefined : accountPurchase?.id}
                        suppliers={unusedSuppliers}
                        readonly={readonly}
                        onClose={this.handleEditModalClose}
                        onSave={this.handleEditModalSave}
                    />
                )}

                {this.state.showPriceComparison && accountPurchase?.id && (
                    <PriceInfoSendDialog
                        accountPurchase={accountPurchase}
                        offers={priceMatrixOffers}
                        currency={currency}
                        customerMedia={customerMedia}
                        customerCount={customerCount}
                        otherAddresses={this.getCombinableAddresses()}
                        onClose={() => this.setState({ showPriceComparison: false })}
                        onSend={this.handleSend(priceMatrixOffers)}
                    />
                )}
            </>
        );
    }
}
